import { api } from './client'

/**
 * O polegar sobre um turno do assistente (demanda 209).
 *
 * 🔴 **Os nomes vêm do enum do back e não são livres.** `Boa`/`Ruim` são o que
 * a coluna guarda; traduzir aqui para `bom`/`ruim` criaria dois vocabulários
 * para o mesmo dado, e a divergência apareceria no dia em que alguém filtrasse
 * pelo valor errado.
 */
export type NotaDaAvaliacao = 'Boa' | 'Ruim'

/** Uma fala do assistente que pode receber nota. */
export type TurnoAvaliavel = {
  mensagemId: number

  /** `Assistente` (o que ele disse) ou `Sistema` (efeito colateral registrado). */
  origem: string
  em: string
  trecho: string

  /** A minha nota, quando já avaliei este turno. */
  minhaNota: NotaDaAvaliacao | null
  meuComentario: string | null
}

export type TurnosDaConversa = {
  conversa: number

  /** A nota sobre a conversa toda — a âncora `mensagemId: 0`. */
  daConversaInteira: { nota: NotaDaAvaliacao; comentario: string | null } | null
  turnos: TurnoAvaliavel[]
}

/**
 * Onde o apontamento está do lado de quem corrige (demanda 235).
 *
 * ⭐ **`NaoSeraTratado` existe para o 👎 não virar promessa.** Não é defeito,
 * depende de decisão do cliente ou fica para depois de 30/09 — com o motivo
 * escrito no próprio item, em vez de ficar "lido" para sempre.
 */
export type EstadoTecnico = 'NaoLido' | 'Lido' | 'Solucionado' | 'NaoSeraTratado'

/** Onde o apontamento está do lado de quem apontou. `Pendente` até alguém conferir. */
export type EstadoDoCliente = 'Pendente' | 'ValidadoOk' | 'NaoValidado'

export const ESTADOS_TECNICOS: EstadoTecnico[] = ['NaoLido', 'Lido', 'Solucionado', 'NaoSeraTratado']
export const ESTADOS_DO_CLIENTE: EstadoDoCliente[] = ['Pendente', 'ValidadoOk', 'NaoValidado']

export type AvaliacaoNaLista = {
  id: number
  conversaExterna: number
  mensagemId: number
  nota: NotaDaAvaliacao
  comentario: string | null
  autor: string
  em: string
  marca: string | null
  assistente: string | null

  /** O que o assistente disse — é o que faz a listagem ser lida sem abrir cada linha. */
  trecho: string

  /**
   * `Assistente` (o cliente leu) ou `Sistema` (nota interna: o cliente NÃO vê) — 209 §8, decisão de 25/09.
   * Nulo na nota da conversa inteira, e em servidor anterior a esta versão.
   */
  origem?: string | null

  estadoTecnico: EstadoTecnico
  tecnicoEm: string | null
  tecnicoPor: string | null
  tecnicoPorId: number | null
  relatoTecnico: string | null
  comoValidar: string | null
  ficha: string | null

  /** O código que estava no ar quando se marcou solucionado — o `/saude` daquele momento. */
  shaNoAr: string | null

  estadoDoCliente: EstadoDoCliente
  clienteEm: string | null
  clientePor: string | null
  relatoDoCliente: string | null

  /**
   * Solucionado e marcado por OUTRA pessoa. 🔴 Quatro olhos: quem corrige não
   * valida a própria correção — o back recusa com 409, e a tela nem oferece.
   */
  podeValidar: boolean
}

export type FiltroDeAvaliacoes = {
  nota?: NotaDaAvaliacao
  assistenteId?: number
  estadoTecnico?: EstadoTecnico
  estadoDoCliente?: EstadoDoCliente
  autorId?: number
  conversaExterna?: number
  ficha?: string

  /** `yyyy-MM-dd`, inclusivo, no fuso de São Paulo. */
  desde?: string
  ate?: string
  pagina?: number
  tamanho?: number
}

export type PaginaDeAvaliacoes = {
  total: number
  podeTriar: boolean
  meuId: number

  /** Sobre TODOS os 👎, ignorando filtros — é o placar que a reunião lê. */
  contagem: {
    tecnico: Record<EstadoTecnico, number>
    cliente: Record<EstadoDoCliente, number>
  }
  autores: { id: number; nome: string }[]
  assistentes: { id: number; nome: string }[]
  itens: AvaliacaoNaLista[]
}

/** Monta a query do `GET /avaliacoes` só com o que foi preenchido. */
export function queryDoFiltro(filtro: FiltroDeAvaliacoes): string {
  const busca = new URLSearchParams()

  for (const [chave, valor] of Object.entries(filtro)) {
    if (valor === undefined || valor === null || valor === '') continue
    busca.set(chave, String(valor))
  }

  return busca.toString()
}

export const avaliacao = {
  /**
   * Os turnos desta conversa, já com a minha nota.
   *
   * ⚠️ Devolve `null` no 204: a conversa existe no Chatwoot e ainda não passou
   * pelo motor. É diferente de erro, e a tela precisa dizer isso.
   */
  turnos: (conversaExterna: number) =>
    api.get<TurnosDaConversa | null>(`/avaliacoes/turnos?conversaExterna=${conversaExterna}`),

  /** Registra ou **corrige** — o mesmo autor sobre o mesmo turno é uma linha só. */
  registrar: (
    conversaExterna: number,
    mensagemId: number,
    nota: NotaDaAvaliacao,
    comentario: string | null,
  ) =>
    api.post<{ id: number; nota: NotaDaAvaliacao; comentario: string | null }>('/avaliacoes', {
      conversaExterna,
      mensagemId,
      nota,
      comentario,
    }),

  apagar: (id: number) => api.delete<void>(`/avaliacoes/${id}`),

  listar: (filtro: FiltroDeAvaliacoes = {}) => {
    const query = queryDoFiltro(filtro)

    return api.get<PaginaDeAvaliacoes>(`/avaliacoes${query ? `?${query}` : ''}`)
  },

  /**
   * Muda o estado técnico de um ou vários apontamentos — **tudo ou nada**.
   *
   * ⚠️ `Solucionado` exige ficha, relato e como validar; `NaoSeraTratado` exige
   * o motivo no relato. A tela confere antes; o back confere de novo.
   */
  triar: (
    ids: number[],
    estado: EstadoTecnico,
    relato: string | null,
    comoValidar: string | null,
    ficha: string | null,
  ) =>
    api.post<{ atualizadas: number }>('/avaliacoes/tecnico', {
      ids,
      estado,
      relato,
      comoValidar,
      ficha,
    }),

  /** A conferência de quem apontou. `NaoValidado` exige relato e reabre o item. */
  validar: (ids: number[], estado: Exclude<EstadoDoCliente, 'Pendente'>, relato: string | null) =>
    api.post<{ atualizadas: number }>('/avaliacoes/cliente', { ids, estado, relato }),
}
